<template>
  <div class="custom-image-wrapper">
    <div class="row">
      <div class="col-4">
        <div class="row">
          <!-- Image Viewer -->
          <div ref="canvasContainer" class="col custom-canvas-container">
            <canvas ref="canvasImage"></canvas>
          </div>
        </div>
        <div class="row">
          <div class="col-5">
            <!-- Image Navigation -->
            <div class="input-group mt-2">
              <button class="btn btn-outline-secondary" @click="prevImage" :disabled="currentIndex === 0">
                <i class="fa-solid fa-caret-left"></i>
              </button>
              <span class="input-group-text">Image {{ currentIndex + 1 }} of {{ images.length }}</span>
              <button class="btn btn-outline-secondary" @click="nextImage" :disabled="currentIndex === images.length - 1">
                <i class="fa-solid fa-caret-right"></i>
              </button>
            </div>
          </div>
          <div class="col pt-2">
            <a href="#" @click="downloadImage" title="download">download</a>
          </div>
          <div class="col-5">
            <!-- Zoom Controls -->
            <div class="custom-commands-right">
              <div class="input-group mt-2">
                <button class="btn btn-outline-secondary" title="zoom out" @click="zoomOut">
                  <i class="fa-solid fa-magnifying-glass-minus"></i>
                </button>
                <button class="btn btn-outline-secondary" title="reset" @click="resetZoom"><i class="fa-solid fa-expand"></i></button>
                <button class="btn btn-outline-secondary" title="zoom in" @click="zoomIn"><i class="fa-solid fa-magnifying-glass-plus"></i></button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col">
        <!-- View Mode -->
        <div class="row">
          <div class="col-2 custom-options">
            <div class="form-check">
              <input
                v-model="viewMode"
                class="form-check-input me-2 custom-checkbox-medium"
                type="radio"
                name="viewMode"
                id="viewMode2"
                value="tables"
                rules="required"
                @change="applyZoom"
              />
              <label class="form-check-label" for="viewMode2"> Tables </label>
            </div>
            <div class="form-check ms-3">
              <input
                v-model="viewMode"
                class="form-check-input me-2 custom-checkbox-medium"
                type="radio"
                name="viewMode"
                id="viewMode1"
                value="words"
                rules="required"
                @change="applyZoom"
              />
              <label class="form-check-label" for="viewMode1"> Words </label>
            </div>
          </div>
          <div class="col-4">
            <div class="custom-status">
              <span class="custom-label me-2">Status:</span>
              <select class="form-select custom-select" v-model="status">
                <option v-for="item in statuses" :key="item.id" :value="item.id">{{ item.name }}</option>
              </select>
            </div>
            <div class="custom-autofill">
              <button
                class="btn btn-primary btn-sm me-2"
                :class="{ 'btn-success': isShowAligned }"
                :disabled="isLoadingAlignment"
                @click="performAlignment"
              >
                <FontAwesomeIcon v-if="isLoadingAlignment" icon="fa-solid fa-spinner" spin />
                <span :class="{ 'ms-2': isLoadingAlignment }">Align Parts</span>
              </button>
              <div>
                <input type="checkbox" class="form-check-input ms-2" v-model="isForce" />
                <span class="custom-force">force?</span>
              </div>
              <div class="custom-info">
                <a
                  v-popover
                  tab-index="0"
                  data-bs-toggle="popover"
                  data-bs-trigger="focus hover"
                  data-bs-html="true"
                  data-bs-sanitize="false"
                  title="Information"
                  :data-bs-content="`You need to have the Invoices AI Chrome Extension installed and enabled. Ensure CCC One is open to the parts invoice screen for this RO. Use the 'force' checkbox to have AI re-evaluate the parts (if they've changed).`"
                  class="btn btn-link"
                  role="button"
                >
                  <i class="fas fa-info-circle"></i>
                </a>
              </div>
            </div>
          </div>
          <div class="col">
            <div v-if="isShowAligned">
              <button v-if="message === null" class="btn btn-primary btn-sm ms-2" @click="performAutoFill">Auto-Fill</button>
            </div>
            <div v-if="message !== null">
              <p class="custom-message">{{ message }}</p>
            </div>
          </div>
        </div>
        <div class="row mt-2">
          <div v-if="isShowAligned" class="col">
            <InvoiceAlignment />
          </div>
          <div v-else class="col">
            <div v-if="selectedTable !== null" class="custom-table">
              <InvoiceTable :page="currentIndex" :idx="polygonIdx" :table="selectedTable" />
            </div>
            <div v-else-if="selectedWord !== null">
              <span>Value:</span>
              <span class="ms-2 me-2">{{ selectedWord }}</span>
              <a href="#" @click.prevent="copy(selectedWord)" class="me-2">
                <FontAwesomeIcon icon="fas fa-clipboard custom-clipboard" />
              </a>
              <FontAwesomeIcon v-if="isCopied" icon="fas fa-check-circle" :style="{ color: 'green' }" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { lookupStore } from '@/stores/lookup'
import { invoiceStore } from '@/stores/invoice'
import axios from 'axios'
import * as pdfjsLib from 'pdfjs-dist'
import InvoiceTable from '@/components/invoice/forms/InvoiceTable.vue'
import InvoiceAlignment from './InvoiceAlignment.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import globals from '@/plugins/globals'
/* global chrome */

export default {
  name: 'InvoiceAnalysis',
  components: {
    InvoiceTable,
    InvoiceAlignment,
    FontAwesomeIcon
  },
  props: {
    url: String,
    pages: {
      type: Array,
      default: () => []
    },
    tables: {
      type: Array,
      default: () => []
    },
    renderedTables: {
      type: Array,
      default: () => []
    },
    isAnalyzing: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      images: [],
      imageSrc: '',
      canvasRef: null,
      ctx: null,
      polygons: [],
      zoomFactor: 1,
      baseScaleFactor: 1,
      baseZoom: 1,
      lastZoom: null,
      currentIndex: 0,
      selectedPolygon: null,
      hoveredPolygon: null,
      dragStart: { x: 0, y: 0 }, // track the starting mouse position for dragging
      imageOffset: { x: 0, y: 0 }, // track the current image position
      debounceTimeout: null, // timeout for debouncing hover events
      isDragging: false, // flag to track if dragging is in progress
      cachedImage: null, // cache the image for performance
      viewMode: 'tables',
      polygonIdx: 0,
      selectedTable: null,
      selectedWord: null,
      isCopied: false,
      currentStatus: null,
      extensionId: 'ceiaedkeibnenkkechoifekepabhcjei',
      isForce: false,
      isShowAligned: false,
      message: null
    }
  },
  mounted() {
    this.canvasRef = this.$refs.canvasImage
    this.ctx = this.canvasRef.getContext('2d')
    this.canvasRef.addEventListener('mousemove', this.handleMouseMove)
    this.canvasRef.addEventListener('click', this.handleCanvasClick)
    this.canvasRef.addEventListener('mousedown', this.handleMouseDown)
    this.canvasRef.addEventListener('mouseup', this.handleMouseUp)
    this.canvasRef.addEventListener('mouseout', this.handleMouseUp)
    this.init()
  },
  emits: ['polygons-drawn'],
  computed: {
    ...mapState(lookupStore, ['lookups']),
    ...mapState(invoiceStore, ['loading', 'invoice', 'invoiceTables', 'alignment']),
    statuses() {
      let _statuses = []
      if (this.lookups) {
        var temp = this.lookups['Status']
        for (var i = 0; i < temp.length; i++) {
          if ([1, 2, 3, 4].includes(temp[i].value)) {
            _statuses.push({
              id: temp[i].value,
              name: temp[i].name
            })
          }
        }
      }
      return _statuses
    },
    status: {
      get() {
        let _status = ''
        if (this.invoice) {
          _status = this.invoice.status
        }
        return _status
      },
      set(value) {
        this.currentStatus = value
        this.saveInvoiceStatus()
      }
    },
    anyAligned() {
      let _is = false
      if (this.alignment) {
        _is = this.alignment.filter((item) => item.alignedTo !== null).length > 0
      }
      return _is
    },
    isNotAligned() {
      let _is = false
      if (this.alignment) {
        _is = this.alignment.filter((item) => item.alignedTo === null).length === this.alignment.length
      }
      return _is
    },
    isLoadingAlignment() {
      return this.loading.alignment
    }
  },
  methods: {
    ...mapActions(invoiceStore, ['resetCells', 'cellAction', 'setInvoice', 'saveStatus', 'alignAutofill']),
    init() {
      console.log('InvoiceAnalysis mounted', {
        url: this.url,
        pages: this.pages,
        tables: this.tables,
        renderedTables: this.renderedTables
      })
      this.message = null
      this.selectedPolygon = null
      this.selectedTable = null
      this.selectedWord = null
      this.lastZoom = null
      this.isShowAligned = false
      this.isCopied = false
      this.isLoadingAlignment = false
      if (this.url !== null && this.url !== undefined) {
        if (this.url !== '') {
          this.downloadFile(this.url)
        }
      }
      this.showAlignment()
    },
    copy(cell) {
      let selectedText = window.getSelection().toString().trim()
      let textToCopy = selectedText || cell
      console.log('textToCopy:', textToCopy)
      if (textToCopy === '') return
      navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
          window.getSelection().removeAllRanges()
          this.isCopied = true
          // if it's a number, add it to our total so far
          if (globals.isNumeric(textToCopy.replace(/[$,]/g, ''))) {
            this.cellAction({
              name: 'total',
              page: this.currentIndex,
              idx: 0,
              value: parseFloat(textToCopy.replace(/[$,]/g, '')),
              key: textToCopy
            })
          }
        })
        .catch((err) => console.error('Clipboard copy failed:', err))
    },
    downloadFile() {
      // pull down the PDF file
      axios.get(this.url, { responseType: 'blob' }).then((response) => {
        const file = new File([response.data], 'file.pdf', { type: 'application/pdf' })
        this.convertPdfToImages(file)
      })
    },
    async convertPdfToImages(file) {
      try {
        const reader = new FileReader()
        reader.onload = async (event) => {
          const pdfData = new Uint8Array(event.target.result)
          const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise
          // clear existing images
          this.images = []
          // loop through and convert each page to an image
          for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            const page = await pdf.getPage(pageNum)
            const scale = this.baseScaleFactor
            const viewport = page.getViewport({ scale })
            const canvas = document.createElement('canvas')
            const context = canvas.getContext('2d')
            canvas.width = viewport.width
            canvas.height = viewport.height
            await page.render({ canvasContext: context, viewport }).promise
            // convert canvas to Blob
            const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
            if (blob) {
              // convert Blob to Object URL
              const imageUrl = URL.createObjectURL(blob)
              this.images.push({ imageUrl }) // Store the image with an empty words array
            }
          }
          // ensure first image loads
          if (this.images.length > 0) {
            this.currentIndex = 0
            this.loadImage()
          }
        }
        reader.readAsArrayBuffer(file)
      } catch (error) {
        console.error('InvoiceAnalysis: error converting PDF to images', error)
      }
    },
    loadImage() {
      if (this.images.length === 0) return
      // use Object URL from images[]
      const imageDataItem = this.images[this.currentIndex]
      this.imageSrc = imageDataItem.imageUrl
      // cache the image
      this.cachedImage = new Image()
      this.cachedImage.src = this.imageSrc
      this.cachedImage.onload = () => {
        this.applyZoom(true)
      }
    },
    applyZoom(initZoom = false) {
      if (!this.ctx || !this.cachedImage) return

      this.polygons = []
      // clear the canvas
      this.ctx.clearRect(0, 0, this.canvasRef.width, this.canvasRef.height)
      const canvas = this.canvasRef
      const container = this.$refs.canvasContainer
      // set canvas width (fit to container width)
      canvas.width = container.clientWidth
      // calculate the scaled height to maintain aspect ratio
      const scaledHeight = this.cachedImage.height * this.zoomFactor
      // set canvas height based on aspect ratio
      canvas.height = scaledHeight
      // redraw the image with the current zoom and offset
      if (initZoom) {
        // calculate the scaling factor to fit the image width-wise
        this.baseZoom = container.clientWidth / this.cachedImage.width / 1.5 // zoom factor to fit 1/2 width
        this.zoomFactor = this.lastZoom !== null ? this.lastZoom : this.baseZoom
        const scaledWidth = this.cachedImage.width * this.zoomFactor
        this.imageOffset.x = (canvas.width - scaledWidth) / 1.5 // Center horizontally
      }
      // apply zoom and offset transformations
      this.ctx.setTransform(this.zoomFactor, 0, 0, this.zoomFactor, this.imageOffset.x, this.imageOffset.y)
      // draw the image at the origin (0, 0) since the offset is already applied in setTransform
      this.ctx.drawImage(this.cachedImage, 0, 0, this.cachedImage.width, this.cachedImage.height)
      // if (this.pages[this.currentIndex]) {
      // set current page height and width
      this.pageWidth = this.pages[this.currentIndex].width || 8.2639
      this.pageHeight = this.pages[this.currentIndex].height || 11.6806
      // load polygons for the current page based upon view mode
      if (this.viewMode === 'tables') {
        this.polygons =
          this.renderedTables
            .filter((table) => table.page === this.currentIndex + 1)
            .map((table) => ({
              content: table.index.toString(),
              polygon: table.polygon
            })) || []
      } else {
        this.polygons = this.pages[this.currentIndex].words || []
        this.resetCells()
      }
      // draw the polygons
      setTimeout(() => {
        this.drawPolygons()
      }, 100)
      // }
    },
    drawPolygons() {
      if (!this.ctx || !this.cachedImage) return
      // clear the canvas
      this.ctx.clearRect(0, 0, this.canvasRef.width, this.canvasRef.height)
      // redraw the image with the current zoom and offset
      this.ctx.setTransform(this.zoomFactor, 0, 0, this.zoomFactor, this.imageOffset.x, this.imageOffset.y)
      this.ctx.drawImage(this.cachedImage, 0, 0, this.cachedImage.width, this.cachedImage.height)
      // calculate scale factors
      const scaleX = this.cachedImage.width / this.pageWidth // scale factor for X-axis
      const scaleY = this.cachedImage.height / this.pageHeight // scale factor for Y-axis
      // draw polygons
      this.polygons.forEach(({ polygon, content }) => {
        const isHovered = this.hoveredPolygon && this.hoveredPolygon.content === content && this.hoveredPolygon.polygon === polygon
        const isSelected = this.selectedPolygon && this.selectedPolygon.content === content && this.selectedPolygon.polygon === polygon
        // do the draw
        this.ctx.beginPath()
        for (let i = 0; i < polygon.length; i += 2) {
          const x = polygon[i] * scaleX // scale X coordinate
          const y = polygon[i + 1] * scaleY // scale Y coordinate
          if (i === 0) {
            this.ctx.moveTo(x, y)
          } else {
            this.ctx.lineTo(x, y)
          }
        }
        this.ctx.closePath()
        // set styles for hover/selection
        this.ctx.strokeStyle = isSelected ? 'blue' : isHovered ? 'yellow' : 'red'
        this.ctx.lineWidth = isSelected ? 3 : 1 // thicker border for selected polygons
        this.ctx.fillStyle = isSelected ? 'rgba(0, 0, 255, 0.2)' : isHovered ? 'rgba(255, 255, 0, 0.3)' : 'rgba(255, 0, 0, 0.2)'
        this.ctx.fill()
        this.ctx.stroke()
      })
    },
    zoomIn() {
      this.lastZoom = this.zoomFactor * 1.2
      this.animateZoom(this.lastZoom)
    },
    zoomOut() {
      this.lastZoom = this.zoomFactor / 1.2
      this.animateZoom(this.lastZoom)
    },
    resetZoom() {
      this.zoomFactor = this.baseZoom
      this.lastZoom = null
      this.applyZoom()
    },
    animateZoom(targetZoom) {
      // animation duration in milliseconds
      const duration = 200
      const startZoom = this.zoomFactor
      const startTime = performance.now()
      // set up the animation steps
      const step = (timestamp) => {
        const progress = Math.min((timestamp - startTime) / duration, 1)
        this.zoomFactor = startZoom + (targetZoom - startZoom) * progress
        this.applyZoom()
        if (progress < 1) {
          requestAnimationFrame(step)
        }
      }
      requestAnimationFrame(step)
    },
    prevImage() {
      this.polygonIdx = 0
      this.selectedTable = null
      if (this.currentIndex > 0) {
        this.currentIndex--
        this.selectedPolygon = null
        this.hoveredPolygon = null
        this.loadImage()
        setTimeout(() => {
          this.drawPolygons()
        }, 100)
      }
    },
    nextImage() {
      this.polygonIdx = 0
      this.selectedTable = null
      if (this.currentIndex < this.images.length - 1) {
        this.currentIndex++
        this.selectedPolygon = null
        this.hoveredPolygon = null
        this.loadImage()
        setTimeout(() => {
          this.drawPolygons()
        }, 100)
      }
    },
    handleCanvasClick(event) {
      this.message = null
      this.isDragging = false
      const { x, y } = this.getMousePosition(event)
      this.selectedPolygon = null
      this.hoveredPolygon = null
      this.polygonIdx = 0
      this.selectedTable = null
      this.selectedWord = null
      this.isCopied = false
      for (const polygonData of this.polygons) {
        if (this.isPointInPolygon(x, y, polygonData.polygon)) {
          this.selectedPolygon = polygonData
          this.hoveredPolygon = null
          this.drawPolygons()
          if (this.viewMode === 'tables') {
            this.polygonIdx = polygonData.content
            this.selectedTable = this.tables[polygonData.content] || []
            this.isShowAligned = false
          } else {
            this.selectedWord = polygonData.content
            this.isShowAligned = false
          }
          return
        }
      }
    },
    handleMouseDown(event) {
      this.isDragging = true
      this.dragStart.x = event.clientX - this.imageOffset.x
      this.dragStart.y = event.clientY - this.imageOffset.y
    },
    handleMouseUp() {
      this.isDragging = false
      this.drawPolygons()
    },
    handleMouseMove(event) {
      if (!this.isDragging) {
        // debounce hover logic
        if (this.debounceTimeout) clearTimeout(this.debounceTimeout)
        this.debounceTimeout = setTimeout(() => {
          const { x, y } = this.getMousePosition(event)
          // handle hover logic
          let isHovering = false
          let hovered = null
          // determine if the mouse is hovering over a polygon
          this.polygons.forEach((polygon) => {
            if (this.isPointInPolygon(x, y, polygon.polygon)) {
              isHovering = true
              hovered = polygon
            }
          })
          if (isHovering) {
            this.hoveredPolygon = hovered
          } else {
            this.hoveredPolygon = null
          }
          this.drawPolygons()
        }, 80) // adjust debounce delay as needed
      } else {
        // handle dragging logic
        this.imageOffset.x = event.clientX - this.dragStart.x
        this.imageOffset.y = event.clientY - this.dragStart.y
        this.redrawImage()
      }
    },
    redrawImage() {
      if (!this.ctx || !this.cachedImage) return
      // clear the canvas
      this.ctx.clearRect(0, 0, this.canvasRef.width, this.canvasRef.height)
      // redraw the image and polygons
      this.ctx.setTransform(this.zoomFactor, 0, 0, this.zoomFactor, this.imageOffset.x, this.imageOffset.y)
      // draw the image at the origin (0, 0) since the offset is already applied in setTransform
      this.ctx.drawImage(this.cachedImage, 0, 0, this.cachedImage.width, this.cachedImage.height)
      // redraw polygons
      this.drawPolygons()
    },
    getMousePosition(event) {
      const rect = this.canvasRef.getBoundingClientRect()
      const mouseX = event.clientX - rect.left
      const mouseY = event.clientY - rect.top
      // get the transformation matrix of the canvas
      const transform = this.ctx.getTransform()
      // invert the transformation matrix to get the correct coordinates
      const invertedTransform = transform.invertSelf()
      const x = invertedTransform.a * mouseX + invertedTransform.c * mouseY + invertedTransform.e
      const y = invertedTransform.b * mouseX + invertedTransform.d * mouseY + invertedTransform.f
      return { x, y }
    },
    isPointInPolygon(x, y, polygon) {
      let inside = false
      const numPoints = polygon.length / 2
      // calculate scale factors
      const scaleX = this.cachedImage.width / this.pageWidth // scale factor for X-axis
      const scaleY = this.cachedImage.height / this.pageHeight // scale factor for Y-axis
      for (let i = 0, j = numPoints - 1; i < numPoints; j = i++) {
        const xi = polygon[i * 2] * scaleX,
          yi = polygon[i * 2 + 1] * scaleY
        const xj = polygon[j * 2] * scaleX,
          yj = polygon[j * 2 + 1] * scaleY
        const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi
        if (intersect) inside = !inside
      }
      return inside
    },
    downloadImage() {
      console.log('downloadImage', this.url)
      window.open(this.url, '_blank')
    },
    async saveInvoiceStatus() {
      if (this.currentStatus) {
        this.setInvoice({
          name: 'status',
          value: this.currentStatus
        })
        // save
        let data = {
          invoice: this.invoice.invoice,
          status: this.currentStatus
        }
        console.log('save status', data)
        await this.saveStatus(data)
      }
    },
    async sendMessageAsync(extensionId, message) {
      return new Promise((resolve, reject) => {
        if (!window.chrome?.runtime?.sendMessage) {
          this.message = 'Chrome extension messaging not available.'
          console.warn('Chrome extension messaging not available.')
          reject(new Error('Chrome extension messaging not available.'))
          return
        }

        console.log('Sending message to extension:', message)
        chrome.runtime.sendMessage(extensionId, message, (response) => {
          if (chrome.runtime.lastError) {
            this.message = 'Error communicating with extension: ' + chrome.runtime.lastError.message
            console.error('Error communicating with extension:', chrome.runtime.lastError.message)
            reject(chrome.runtime.lastError)
          } else {
            console.log('Received response from extension:', response)
            resolve(response)
          }
        })
      })
    },
    async performAlignment() {
      this.message = null
      try {
        if (this.isNotAligned || this.isForce) {
          console.log('calling for Parts from CCC One...')
          let response = await this.sendMessageAsync(this.extensionId, {
            action: 'capturelines',
            data: null
          })
          let proceed = false
          let parts = null
          // if we have parts, send for alignment
          if (response) {
            if (response.result) {
              if (response.result.length > 0) {
                parts = response.result
                proceed = true
              }
            }
          }
          if (proceed) {
            console.log('Parts:', parts)
            let tables = null
            if (this.invoiceTables) {
              tables = this.invoiceTables
            }
            let data = {
              invoice: this.invoice.invoice,
              foundParts: JSON.stringify(parts),
              invoiceItems: JSON.stringify(tables)
            }
            console.log('call for alignment', data)
            await this.alignAutofill(data)
          } else {
            this.message =
              'No parts found, please ensure the Invoices AI Chrome Extension is installed and enabled. Also, ensure CCC One is open to the parts invoice screen for this RO.'
            console.log('No parts found')
          }
        } else {
          console.log('Already aligned')
        }
        this.isForce = false
        this.showAlignment()
        // const response = await sendMessageAsync(this.extensionId, {
        //   action: 'updateline',
        //   data: {
        //     lineNumber: '8',
        //     fieldUpdates: {
        //       partNumber: 'NEWPART1234',
        //       invoiceListPrice: '150.00',
        //       partQuantity: '1'
        //     }
        //   }
        // })
        // console.log('Extension responded:', response)
      } catch (err) {
        console.error('Error communicating with extension:', err.message)
      }
    },
    showAlignment() {
      this.isShowAligned = false
      if (this.anyAligned) {
        this.isShowAligned = true
      }
      if (this.onInvoicePage()) {
        console.log('Invoice Page')
      }
    },
    async onInvoicePage() {
      try {
        const response = await this.sendMessageAsync(this.extensionId, {
          action: 'invoicepage',
          data: null
        })
        if (response?.invoiceTableCard) {
          console.log("You're on the correct Invoice page!")
          return true
        } else {
          console.log('Not on the Invoice page.')
          return false
        }
      } catch (err) {
        console.warn('Extension error:', err.message)
        return false
      }
    },
    performAutoFill() {
      console.log('Perform AutoFill', this.alignment)
    }
  },
  beforeUnmount() {
    this.canvasRef.removeEventListener('mousemove', this.handleMouseMove)
    this.canvasRef.removeEventListener('click', this.handleCanvasClick)
    this.canvasRef.removeEventListener('mousedown', this.handleMouseDown)
    this.canvasRef.removeEventListener('mouseup', this.handleMouseUp)
    this.canvasRef.removeEventListener('mouseout', this.handleMouseUp)
  }
}
</script>
<style lang="scss" scoped>
.custom-image-wrapper {
  overflow-x: hidden;
  overflow-y: auto;
  max-height: 80vh;
  padding-right: 5px;
}
.custom-image-viewer {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.custom-canvas-container {
  border: 1px solid #ddd;
  width: 50vw;
  height: 60vh;
  overflow: hidden;
  padding: 10px;
}
.custom-commands-right {
  float: right;
  width: 140px;
}

.custom-options {
  font-size: 1em;
  font-weight: 500;
  display: inline-flex;
  padding: 10px;
}
.custom-table {
  overflow-x: hidden;
  overflow-y: auto;
  height: 450px;
  max-height: 450px;
}
.custom-clipboard {
  color: black;
  transition: color 0.3s ease-in-out;
}

.custom-clipboard-copied {
  color: green !important;
}
.custom-status {
  display: inline-flex;
  border: 1px solid #008106;
  padding: 5px;
  height: 60px;
}
.custom-select {
  width: 150px;
  margin-left: 10px;
}
.custom-autofill {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  margin-left: 15px;
}
.custom-info {
  position: absolute;
  top: -10px;
  right: -30px;
}
.custom-force {
  font-size: 0.8em;
  margin-left: 10px;
}
.custom-message {
  font-size: 0.8em;
  color: red;
  margin-left: 10px;
}
</style>
